// src/services/queries/portfolioQueries.ts

import type { QueryFunction, QueryKey } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import { portfolioQueryKeys } from "@/constants/queryKeys";
import type {
  GetTransactionsResponse,
  GetHoldingsResponse,
  GetRealizedPnlResponse,
  PortfolioNavResponse,
  PortfolioPerformanceVsBenchmarkWindowedResponse,
  AddTransactionRequest,
  AddTransactionResponse,
  UpdateTransactionRequest,
  UpdateTransactionResponse,
  DeleteTransactionResponse,
} from "@/types/portfolio";

// Extracts the most informative error message from a failed fetch Response
const extractErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const cloned = response.clone();
    // Prefer JSON when possible
    try {
      const data = await cloned.json();
      const detail = (data && (data.detail || data.message)) as unknown;
      if (typeof detail === 'string' && detail.trim().length > 0) return detail;
    } catch {
      // Not JSON; fall through to text
    }
    const text = await response.text();
    if (text && text.trim().length > 0) {
      // Sometimes servers send JSON with wrong content-type
      try {
        const parsed = JSON.parse(text);
        const detail = (parsed && (parsed.detail || parsed.message)) as unknown;
        if (typeof detail === 'string' && detail.trim().length > 0) return detail;
      } catch {
        // Plain text body
      }
      return text.trim();
    }
  } catch {
    // ignore parsing errors
  }
  return fallback;
};

// GET ALL TRANSACTIONS
export const fetchTransactions: QueryFunction<
  GetTransactionsResponse,
  QueryKey
> = async ({ signal }) => {
  const url = new URL(`${API_BASE_URL}/portfolio/transactions`);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch transactions: ${response.status}`);
  }

  return response.json() as Promise<GetTransactionsResponse>;
};

export const transactionsQueryOptions = (authToken?: string) => ({
  queryKey: portfolioQueryKeys.transactions.list(),
  // Inline queryFn to inject Authorization header
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/portfolio/transactions`);

    const response = await fetch(url.toString(), {
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch transactions: ${response.status}`);
    }

    return response.json() as Promise<GetTransactionsResponse>;
  }) as QueryFunction<GetTransactionsResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// (Removed) Historical portfolio performance API

// GET PORTFOLIO NAV
export const fetchPortfolioNav: QueryFunction<
  PortfolioNavResponse,
  QueryKey
> = async ({ signal }) => {
  const url = new URL(`${API_BASE_URL}/portfolio/nav`);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch portfolio NAV: ${response.status}`);
  }

  return response.json() as Promise<PortfolioNavResponse>;
};

export const portfolioNavQueryOptions = (
  authToken?: string
) => ({
  queryKey: portfolioQueryKeys.nav.data(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/portfolio/nav`);

    const response = await fetch(url.toString(), {
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch portfolio NAV: ${response.status}`);
    }

    return response.json() as Promise<PortfolioNavResponse>;
  }) as QueryFunction<PortfolioNavResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// GET PORTFOLIO PERFORMANCE VS BENCHMARK (BTC)
export const fetchPortfolioPerformanceVsBenchmark: QueryFunction<
  PortfolioPerformanceVsBenchmarkWindowedResponse,
  QueryKey
> = async ({ signal }) => {
  const url = new URL(`${API_BASE_URL}/portfolio/performance`);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch portfolio performance vs benchmark: ${response.status}`
    );
  }

  return response.json() as Promise<PortfolioPerformanceVsBenchmarkWindowedResponse>;
};

export const portfolioPerformanceVsBenchmarkQueryOptions = (
  authToken?: string
) => ({
  queryKey: portfolioQueryKeys.performanceVsBenchmark.data(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/portfolio/performance`);

    const response = await fetch(url.toString(), {
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch portfolio performance vs benchmark: ${response.status}`
      );
    }

    return response.json() as Promise<PortfolioPerformanceVsBenchmarkWindowedResponse>;
  }) as QueryFunction<PortfolioPerformanceVsBenchmarkWindowedResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// GET HOLDINGS
export const fetchPortfolioHoldings: QueryFunction<
  GetHoldingsResponse,
  QueryKey
> = async ({ signal }) => {
  const url = new URL(`${API_BASE_URL}/portfolio/holdings`);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch holdings: ${response.status}`);
  }

  return response.json() as Promise<GetHoldingsResponse>;
};

export const portfolioHoldingsQueryOptions = (authToken?: string) => ({
  queryKey: portfolioQueryKeys.holdings.list(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/portfolio/holdings`);

    const response = await fetch(url.toString(), {
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch holdings: ${response.status}`);
    }

    return response.json() as Promise<GetHoldingsResponse>;
  }) as QueryFunction<GetHoldingsResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// GET REALIZED PNL
export const fetchRealizedPnl: QueryFunction<
  GetRealizedPnlResponse,
  QueryKey
> = async ({ signal }) => {
  const url = new URL(`${API_BASE_URL}/portfolio/realized-pnl`);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch realized PnL: ${response.status}`);
  }

  return response.json() as Promise<GetRealizedPnlResponse>;
};

export const realizedPnlQueryOptions = (authToken?: string) => ({
  queryKey: portfolioQueryKeys.realized.list(),
  queryFn: (async ({ signal }) => {
    const url = new URL(`${API_BASE_URL}/portfolio/realized-pnl`);

    const response = await fetch(url.toString(), {
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch realized PnL: ${response.status}`);
    }

    return response.json() as Promise<GetRealizedPnlResponse>;
  }) as QueryFunction<GetRealizedPnlResponse, QueryKey>,
  enabled: Boolean(authToken),
});

// MUTATION FUNCTIONS

// ADD TRANSACTION
export const addTransaction = async (data: AddTransactionRequest, authToken?: string): Promise<AddTransactionResponse> => {
  const url = new URL(`${API_BASE_URL}/portfolio/transactions`);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to add transaction: ${response.status}`);
    throw new Error(message);
  }

  return response.json() as Promise<AddTransactionResponse>;
};

// UPDATE TRANSACTION
export const updateTransaction = async (
  transactionId: string,
  data: UpdateTransactionRequest,
  authToken?: string
): Promise<UpdateTransactionResponse> => {
  const url = new URL(`${API_BASE_URL}/portfolio/transactions/${transactionId}`);

  const response = await fetch(url.toString(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to update transaction: ${response.status}`);
    throw new Error(message);
  }

  return response.json() as Promise<UpdateTransactionResponse>;
};

// DELETE TRANSACTION
export const deleteTransaction = async (transactionId: string, authToken?: string): Promise<DeleteTransactionResponse> => {
  const url = new URL(`${API_BASE_URL}/portfolio/transactions/${transactionId}`);

  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response, `Failed to delete transaction: ${response.status}`);
    throw new Error(message);
  }

  return response.json() as Promise<DeleteTransactionResponse>;
};
